import React, { useEffect, useMemo } from 'react';

import { useSyncSystem } from '@/lib/sync/sync-system-context';
import { getClientId, getClientGroupId, CLIENT_PLATFORM } from '@/lib/sync/client-identity';
import { useAuthStore } from '@/stores/use-auth-store';
import { setSyncProvider } from '@/lib/sync/workspace-accessor';
import { workspaceDocId } from '@/utils/workspace-doc-id';
import { collabColorFromId } from '@/utils/collab-color';
import { entityProjection } from '@/lib/sync/entity-projection';
import { SessionProvider, LocalSessionAdapter, LruTracker } from '@/session';
import { useYjsRegistry } from '@/lib/sync/hooks/yjs-registry-context';

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:3000/ws/sync';

// ---------------------------------------------------------------------------
// WorkspaceSubscriber — attaches entityProjection to the workspace Yjs doc
// ---------------------------------------------------------------------------

export function WorkspaceSubscriber() {
  const sys = useSyncSystem();
  const user = useAuthStore((s) => s.user);
  const wsDocId = useMemo(() => (user?.sub ? workspaceDocId(user.sub) : null), [user?.sub]);

  useEffect(() => {
    if (!wsDocId) return;
    setSyncProvider(sys, wsDocId);

    // Attach projection to entityStore — no Y.Doc needed
    entityProjection.attach();

    // Wire remote deletion detection: when a doc disappears from entityStore
    // and we still have it subscribed, it was deleted by another client.
    entityProjection.setOnDocDisappeared((id) => {
      if (sys.registry.getEntry(id)) sys.registry.markDeleted(id, 'remote');
    });

    // Bootstrap/catch-up now happens via WS sync frames (authOk → sendSync in SyncSystemContext).

    return () => {
      entityProjection.setOnDocDisappeared(null);
      entityProjection.detach();
    };
  }, [sys, wsDocId]);

  return null;
}

// ---------------------------------------------------------------------------
// AuthSyncBridge — connects the WebSocket on token change.
//
// Lives INSIDE SyncSystemProvider so it can reach transport via useSyncSystem.
// WorkspaceBootstrap has already populated useWorkspaceStore by the time
// this fires (the provider only mounts once activeWorkspaceId is non-null).
// ---------------------------------------------------------------------------

export function AuthSyncBridge() {
  const { transport, registry } = useSyncSystem();
  const token = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (token) {
      transport.connect(WS_URL, token);
    } else {
      void transport.disconnect();
    }
  }, [transport, token]);

  useEffect(() => {
    if (user) {
      const userInfo = {
        id: user.sub,
        name: user.name,
        color: collabColorFromId(user.sub ?? 'anonymous'),
        avatar: user.picture ?? null,
        clientId: getClientId(),
        clientGroupId: getClientGroupId(),
        clientPlatform: CLIENT_PLATFORM,
      };
      // Set user on all currently subscribed docs' awareness
      for (const id of registry.getAllIds()) {
        registry.getAwareness(id)?.setLocalStateField('user', userInfo);
      }
    }
  }, [transport, registry, user]);

  return null;
}

// ---------------------------------------------------------------------------
// SessionWrapper — wires SessionProvider with LruTracker.
//
// Must be rendered inside WorkspaceServiceProvider (for YjsRegistryContext).
// ---------------------------------------------------------------------------

export function SessionWrapper({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isAuthLoading = useAuthStore((s) => s.isLoading);
  // Wait for auth to resolve so userId is stable — prevents SessionProvider from
  // mounting twice (once as 'anonymous', once as the real user), which would drain
  // the deep-link queue into the wrong session instance.
  const userId = isAuthLoading ? null : (user?.sub ?? 'anonymous');
  const registry = useYjsRegistry();
  const adapter = useMemo(() => userId ? new LocalSessionAdapter(userId) : null, [userId]);
  const lruTracker = useMemo(
    () => userId ? new LruTracker(userId, registry) : null,
    [userId, registry],
  );
  if (!adapter || !lruTracker) return <>{children}</>;
  return (
    <SessionProvider adapter={adapter} lruTracker={lruTracker}>
      {children}
    </SessionProvider>
  );
}
